/**
 * Customer.io Analytics Provider
 * Implements the AnalyticsProvider interface for Customer.io Track.js
 */

import type { AnalyticsProvider } from "@artha/types";

/* eslint-disable @typescript-eslint/no-explicit-any */

export class CustomerIOProvider implements AnalyticsProvider {
  readonly name = "customerio";
  readonly enabled: boolean;
  private siteId: string;

  constructor(enabled: boolean = true, siteId: string = "") {
    this.enabled = enabled;
    this.siteId = siteId;
  }

  private getCIO(): any | null {
    if (typeof window === "undefined") return null;
    return (window as any)._cio || null;
  }

  async identify(userId: string, properties?: Record<string, any>): Promise<void> {
    if (!this.enabled) return;
    this.getCIO()?.identify({ id: userId, ...properties });
  }

  async track(eventName: string, properties?: Record<string, any>): Promise<void> {
    if (!this.enabled) return;
    this.getCIO()?.track(eventName, properties);
  }

  async page(pageName?: string, properties?: Record<string, any>): Promise<void> {
    if (!this.enabled) return;
    this.getCIO()?.page(pageName, properties);
  }

  async group(_groupType: string, _groupId: string, _properties?: Record<string, any>): Promise<void> {
    // Customer.io (track.js) doesn't have a native group() concept like PostHog
    // We handle grouping via traits in identify or custom events if needed
    if (!this.enabled) return;
  }

  async alias(_newId: string, _previousId?: string): Promise<void> {
    // Customer.io handles identity merging automatically based on IDs
    if (!this.enabled) return;
  }

  async reset(): Promise<void> {
    if (!this.enabled) return;
    this.getCIO()?.reset();
  }

  async initialize(): Promise<void> {
    if (typeof window === "undefined" || !this.enabled || !this.siteId) return;

    // Check if already loaded
    if ((window as any)._cio && (window as any)._cio.load) return;

    // Set up the _cio queue
    const cio: any[] & Record<string, any> = (window as any)._cio || [];
    (window as any)._cio = cio;

    const methods = ["load", "identify", "sidentify", "track", "page", "reset", "on", "off"];
    for (const method of methods) {
      if (!cio[method]) {
        cio[method] = (...args: any[]) => cio.push([method, ...args]);
      }
    }

    // Load the official track.js script
    const script = document.createElement("script");
    script.async = true;
    script.id = "cio-tracker";
    script.setAttribute("data-site-id", this.siteId);
    script.setAttribute("data-use-array-params", "true");
    script.setAttribute("data-use-in-app", "true");
    script.src = "https://assets.customer.io/assets/track.js";
    document.head.appendChild(script);
  }
}

export function createCustomerIOProvider(enabled: boolean = true, siteId: string = ""): CustomerIOProvider {
  return new CustomerIOProvider(enabled, siteId);
}
